import { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare,
  Radio,
  Target,
  CheckSquare,
  FileText,
  History,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  X,
  Sparkles,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types & data                                                       */
/* ------------------------------------------------------------------ */

interface GuidedTourProps {
  isOpen: boolean;
  onClose: () => void;
  onStepChange?: (step: number) => void;
}

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  accentColor: string; // tailwind color token, e.g. "primary" | "success"
  glowFrom: string; // gradient start
  glowTo: string; // gradient end
}

const TOUR_STEPS: TourStep[] = [
  {
    id: 'chat',
    title: 'Chat',
    description:
      'Talk to ZoroAgent in natural language. Ask about threats, request investigations, or give commands. The agent understands security context and can take action on your behalf.',
    icon: <MessageSquare className="w-7 h-7" />,
    accentColor: 'primary',
    glowFrom: 'from-blue-500',
    glowTo: 'to-cyan-400',
  },
  {
    id: 'monitoring',
    title: 'Monitoring',
    description:
      'Real-time threat detection and response. Watch as ZoroAgent processes alerts autonomously, triages incidents, and escalates what matters most.',
    icon: <Radio className="w-7 h-7" />,
    accentColor: 'success',
    glowFrom: 'from-emerald-500',
    glowTo: 'to-teal-400',
  },
  {
    id: 'tasks',
    title: 'Tasks',
    description:
      'View and manage security tasks. ZoroAgent tracks investigations and action items so nothing falls through the cracks.',
    icon: <Target className="w-7 h-7" />,
    accentColor: 'warning',
    glowFrom: 'from-amber-500',
    glowTo: 'to-orange-400',
  },
  {
    id: 'approvals',
    title: 'Approvals',
    description:
      'High-risk actions need your approval. Review what ZoroAgent wants to do before it acts -- keeping humans in the loop for critical decisions.',
    icon: <CheckSquare className="w-7 h-7" />,
    accentColor: 'danger',
    glowFrom: 'from-rose-500',
    glowTo: 'to-pink-400',
  },
  {
    id: 'policies',
    title: 'Policies',
    description:
      'Define the rules. Set what ZoroAgent can and cannot do using YAML policies. Fine-tune autonomy levels per action category.',
    icon: <FileText className="w-7 h-7" />,
    accentColor: 'primary',
    glowFrom: 'from-violet-500',
    glowTo: 'to-purple-400',
  },
  {
    id: 'audit',
    title: 'Audit',
    description:
      'Full transparency. Every action ZoroAgent takes is logged here with reasoning, timestamps, and outcome -- ready for compliance review.',
    icon: <History className="w-7 h-7" />,
    accentColor: 'warning',
    glowFrom: 'from-yellow-500',
    glowTo: 'to-amber-400',
  },
  {
    id: 'reports',
    title: 'Reports',
    description:
      'Analytics and insights. Track security posture, response times, and ROI with auto-generated dashboards and exportable data.',
    icon: <BarChart3 className="w-7 h-7" />,
    accentColor: 'success',
    glowFrom: 'from-cyan-500',
    glowTo: 'to-blue-400',
  },
];

const STORAGE_KEY = 'zoroagent_tour_complete';

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function GuidedTour({ isOpen, onClose, onStepChange }: GuidedTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  // Unique key to force re-mount of the card for the transition animation
  const [animKey, setAnimKey] = useState(0);

  const step = TOUR_STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === TOUR_STEPS.length - 1;

  /* ---- helpers ---- */

  const completeTour = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onClose();
  }, [onClose]);

  const goTo = useCallback(
    (index: number, dir: 'next' | 'prev') => {
      if (index < 0 || index >= TOUR_STEPS.length) return;
      setDirection(dir);
      setAnimKey(k => k + 1);
      setCurrentStep(index);
      onStepChange?.(index);
    },
    [onStepChange],
  );

  const handleNext = useCallback(() => {
    if (isLast) {
      completeTour();
    } else {
      goTo(currentStep + 1, 'next');
    }
  }, [currentStep, isLast, completeTour, goTo]);

  const handlePrev = useCallback(() => {
    if (!isFirst) goTo(currentStep - 1, 'prev');
  }, [currentStep, isFirst, goTo]);

  /* ---- keyboard ---- */

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        completeTour();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, handleNext, handlePrev, completeTour]);

  /* ---- reset when opened ---- */

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setAnimKey(0);
      setDirection('next');
    }
  }, [isOpen]);

  /* ---- render ---- */

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={completeTour} />

      {/* Spotlight ring -- decorative glow that matches the current step color */}
      <div
        className={`absolute w-[420px] h-[420px] rounded-full bg-gradient-to-br ${step.glowFrom} ${step.glowTo} opacity-[0.07] blur-3xl pointer-events-none transition-all duration-700`}
      />

      {/* Tooltip card */}
      <div
        key={animKey}
        className="relative z-10 w-full max-w-md mx-4"
        style={{
          animation:
            direction === 'next'
              ? 'tour-slide-in-right 0.4s cubic-bezier(0.16, 1, 0.3, 1) both'
              : 'tour-slide-in-left 0.4s cubic-bezier(0.16, 1, 0.3, 1) both',
        }}
      >
        <div className="glass rounded-2xl border border-slate-600/50 shadow-2xl overflow-hidden">
          {/* Top accent gradient bar */}
          <div className={`h-1 w-full bg-gradient-to-r ${step.glowFrom} ${step.glowTo}`} />

          <div className="p-8">
            {/* Header row: step counter + close */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary-400" />
                <span className="text-xs font-semibold text-slate-400 tracking-wider uppercase">
                  Step {currentStep + 1} of {TOUR_STEPS.length}
                </span>
              </div>
              <button
                onClick={completeTour}
                className="btn btn-ghost p-1.5 rounded-lg"
                title="Close tour"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-5">
              <div
                className={`relative p-4 rounded-2xl bg-gradient-to-br ${step.glowFrom} ${step.glowTo} text-white shadow-lg`}
              >
                {/* Outer glow ring */}
                <div
                  className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${step.glowFrom} ${step.glowTo} opacity-40 blur-xl -z-10`}
                />
                {step.icon}
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-white text-center mb-3">{step.title}</h2>

            {/* Description */}
            <p className="text-sm leading-relaxed text-slate-300 text-center mb-8">
              {step.description}
            </p>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {TOUR_STEPS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i, i > currentStep ? 'next' : 'prev')}
                  className={`rounded-full transition-all duration-300 ${
                    i === currentStep
                      ? `w-6 h-2 bg-gradient-to-r ${step.glowFrom} ${step.glowTo}`
                      : i < currentStep
                        ? 'w-2 h-2 bg-slate-500'
                        : 'w-2 h-2 bg-slate-700'
                  }`}
                  aria-label={`Go to step ${i + 1}`}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <button
                onClick={completeTour}
                className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                Skip Tour
              </button>

              <div className="flex items-center gap-2">
                {!isFirst && (
                  <button
                    onClick={handlePrev}
                    className="btn btn-ghost flex items-center gap-1 text-sm px-3 py-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="btn btn-primary flex items-center gap-1 text-sm px-5 py-2"
                >
                  {isLast ? 'Finish' : 'Next'}
                  {!isLast && <ChevronRight className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Simulated nav-tab spotlight (visual only) */}
        <div className="flex justify-center mt-6">
          <div
            className={`
              inline-flex items-center gap-2 px-4 py-2.5 rounded-xl
              bg-slate-800/80 border border-slate-600/60
              shadow-[0_0_24px_rgba(59,130,246,0.25)]
              backdrop-blur-xl
              transition-all duration-500
            `}
            style={{
              boxShadow: `0 0 24px ${
                step.accentColor === 'primary'
                  ? 'rgba(59,130,246,0.35)'
                  : step.accentColor === 'success'
                    ? 'rgba(16,185,129,0.35)'
                    : step.accentColor === 'warning'
                      ? 'rgba(245,158,11,0.35)'
                      : 'rgba(239,68,68,0.35)'
              }`,
            }}
          >
            <span
              className={`${
                step.accentColor === 'primary'
                  ? 'text-primary-400'
                  : step.accentColor === 'success'
                    ? 'text-success-400'
                    : step.accentColor === 'warning'
                      ? 'text-warning-400'
                      : 'text-danger-400'
              }`}
            >
              {step.icon}
            </span>
            <span className="text-sm font-medium text-white">{step.title}</span>
            <span className="relative flex h-2 w-2 ml-1">
              <span
                className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  step.accentColor === 'primary'
                    ? 'bg-primary-400'
                    : step.accentColor === 'success'
                      ? 'bg-success-400'
                      : step.accentColor === 'warning'
                        ? 'bg-warning-400'
                        : 'bg-danger-400'
                }`}
              />
              <span
                className={`relative inline-flex rounded-full h-2 w-2 ${
                  step.accentColor === 'primary'
                    ? 'bg-primary-500'
                    : step.accentColor === 'success'
                      ? 'bg-success-500'
                      : step.accentColor === 'warning'
                        ? 'bg-warning-500'
                        : 'bg-danger-500'
                }`}
              />
            </span>
          </div>
        </div>
      </div>

      {/* Inline keyframes -- avoids needing to touch global CSS or tailwind config */}
      <style>{`
        @keyframes tour-slide-in-right {
          0% {
            opacity: 0;
            -webkit-transform: translateX(40px) scale(0.97);
            transform: translateX(40px) scale(0.97);
          }
          100% {
            opacity: 1;
            -webkit-transform: translateX(0) scale(1);
            transform: translateX(0) scale(1);
          }
        }
        @keyframes tour-slide-in-left {
          0% {
            opacity: 0;
            -webkit-transform: translateX(-40px) scale(0.97);
            transform: translateX(-40px) scale(0.97);
          }
          100% {
            opacity: 1;
            -webkit-transform: translateX(0) scale(1);
            transform: translateX(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}
